import { type FunctionComponent, useState } from 'react'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

type Props = {
  seatName: string
  /** Whether the seat still sends bid params for an ad unit. Answered when the dialog opens, not before. */
  isMapped: boolean
  /** Already prefixed by the caller, e.g. `pbs-bidder-seat-delete`. */
  trackingPrefix: TrackingLabel
  onDelete: () => Promise<void>
}

/**
 * What happens when Delete is clicked in a seat's danger zone: the mapping is checked, and the answer
 * decides which of two cards this is.
 *
 * The button that opens it is never disabled on mapping status. A greyed-out control has to explain
 * itself in a tooltip nobody opens, whereas the seat's mapping is the one thing standing between the
 * publisher and what they came to do — so the dialog says it in a sentence they cannot miss, and the way
 * out is the mapping screen they already know.
 *
 * When nothing is mapped, the name has to be typed back first, the same as archiving an ad unit or a
 * context. It matters more here: a seat cannot be archived, and there is no list of deleted seats to
 * bring one back from.
 *
 * The pending state is held here rather than passed in, because the props of a globally opened modal are
 * captured when it opens — a caller's `isPending` would still read false for the whole call. A delete
 * that fails leaves the card up with Cancel working and the error toast beside it. The caller closes on
 * success.
 */
export const DeleteBidderSeatModal: FunctionComponent<Props> = ({ seatName, isMapped, trackingPrefix, onDelete }) => {
  const { close } = useGlobalModal()
  const [typedName, setTypedName] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const isNameConfirmed = isPhraseTyped(seatName, typedName)

  const handleDelete = async () => {
    // The disabled button is the affordance; not deleting is the invariant.
    if (!isNameConfirmed || isDeleting) {
      return
    }
    setIsDeleting(true)
    try {
      await onDelete()
    } catch {
      // Reported by the mutation's error toast. Swallowed so the click handler settles and the card comes
      // back enabled for another try.
      setIsDeleting(false)
    }
  }

  if (isMapped) {
    return (
      <Modal.Content>
        <Modal.Title>{seatName} is still mapped</Modal.Title>
        <Modal.Body>
          <div className={'flex flex-col gap-3'}>
            <p>This seat still sends bid params for at least one ad unit, and deleting it would take that mapping with it.</p>
            <p>Remove it from the medias it is mapped on, then delete the seat.</p>
          </div>
        </Modal.Body>
        <Modal.Actions>
          <Button variant={'primary'} onClick={close} trackingLabel={`${trackingPrefix}-mapped-close`}>
            Got it
          </Button>
        </Modal.Actions>
      </Modal.Content>
    )
  }

  return (
    <Modal.Content>
      <Modal.Title>Delete {seatName}</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>The bidder stops being called. Its mapping is deleted with it, and it leaves every bidder rule that holds it.</p>
          <p>Unlike an ad unit or a media, a seat cannot be archived — this cannot be undone.</p>
          <ConfirmationPhraseField phrase={seatName} value={typedName} onChange={setTypedName} disabled={isDeleting} trackingLabel={`${trackingPrefix}-type`} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} disabled={isDeleting} trackingLabel={`${trackingPrefix}-cancel`}>
          Cancel
        </Button>
        <Button
          variant={'negative'}
          iconName={isDeleting ? 'pubstack' : 'delete'}
          iconSpin={isDeleting}
          disabled={!isNameConfirmed || isDeleting}
          onClick={handleDelete}
          trackingLabel={`${trackingPrefix}-confirm`}
        >
          Delete seat
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
